// Flags on the check (the Quick check pack; Spencer's approvals of 2026-10-23, rows Q2 to Q6; docs/quick-check-pack-brief.md).
// A flag is one record (ug.job_flag): what must change, in Spencer's words or a finding's, on an item, a wall or the room;
// an optional note to the engineer; who closed it and the engineer's note. "Needs a fix" on a finding raises one with the
// finding's text; "+ Flag" raises one by hand. It reaches the engineer through the connector's queue; he closes it with a
// note; the item's next check clears the "fixed" chip. The pieces here are shared by the Quick table, the item panel and
// the walk bar. Nothing here prices anything.

import { useState } from 'react';
import type { FlagRow } from '@zerogap/engine';
import { refresh, rpc, toast, useRows, type Row } from '../../../lib/api';
import type { JobCtx } from '../Workspace';

export const mmdd = (v: unknown) => {
  if (!v) return '';
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? String(v).slice(5, 10) : `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** The job's flags, every one (open, closed, cleared): the engine leaves the cleared ones out. */
export function useFlags(jobId: string): FlagRow[] {
  const rows = useRows('job_flag', { eq: { job_id: jobId }, order: ['created_at'], enabled: !!jobId });
  return rows.rows as unknown as FlagRow[];
}

/** The seats, for who a flag waits on. */
export function useSeats(): Row[] {
  return useRows('pipeline_seat', { order: ['sort'] }).rows;
}

export const userName = (users: Row[], id: string | null | undefined, fallback = 'Spencer') => (id ? String(users.find((u) => u.id === id)?.name ?? fallback) : fallback);

/** Who a flag waits on: the holder of its seat, by name (the seat's name when nobody holds it). */
export function waitsOn(flag: FlagRow, seats: Row[], users: Row[]): string {
  const seat = seats.find((s) => s.id === flag.seat_id);
  const holder = seat?.holder_user_id ? users.find((u) => u.id === seat.holder_user_id) : null;
  return holder?.name ? String(holder.name) : seat?.name ? String(seat.name) : 'the engineer';
}

export async function raiseFlag(ctx: JobCtx, f: { target: 'item' | 'wall' | 'room'; item?: string | null; view?: string | null; text: string; note?: string | null; findingKey?: string | null }): Promise<string> {
  const id = await rpc<string>('flag_raise', { p_job: ctx.jobId, p_target: f.target, p_item: f.item ?? null, p_view: f.view ?? null, p_text: f.text, p_note: f.note ?? null, p_finding_key: f.findingKey ?? null });
  refresh('job_flag', 'job_item');
  return id;
}
export async function undoFlag(id: string) {
  await rpc('flag_undo', { p_id: id });
  refresh('job_flag', 'job_item');
}
export async function clearFlag(id: string) {
  await rpc('flag_clear', { p_id: id });
  refresh('job_flag');
}
export async function noteFlag(id: string, note: string) {
  await rpc('flag_note', { p_id: id, p_note: note });
  refresh('job_flag');
}

/**
 * A flag's state as a chip with who and when (Q2, Q6): open, "Needs a fix" (on a finding) or "flag" (by hand) and "Spencer ·
 * 10-23 · waits on Bennett"; closed, "fixed · Bennett · 10-24" in the done colour with the engineer's note.
 */
export function FlagState({ flag, users, seats, onFinding, brief }: { flag: FlagRow; users: Row[]; seats: Row[]; onFinding?: boolean; /** The chip alone, as the Quick row shows it: the note stays on the item panel (Q6). */ brief?: boolean }) {
  if (flag.resolved_at) {
    return (
      <>
        <span className="tag fixed" title={brief && flag.resolution_note ? `${flag.resolution_note} · checking the item clears it` : 'The engineer closed this flag; checking the item clears it'}>
          fixed · {userName(users, flag.resolved_by, 'the engineer')} · {mmdd(flag.resolved_at)}
        </span>
      </>
    );
  }
  return (
    <>
      <span className={`tag ${onFinding ? 'fix' : 'flg'}`}>{onFinding ? 'Needs a fix' : 'flag'}</span>
      <span className="st">
        {userName(users, flag.created_by)} · {mmdd(flag.created_at)} · waits on {waitsOn(flag, seats, users)}
      </span>
    </>
  );
}

/** The engineer's closing note, under the flag's own words so neither squeezes the other (Iris, 2026-10-24). */
export function ClosingNote({ flag, users }: { flag: FlagRow; users: Row[] }) {
  if (!flag.resolved_at || !flag.resolution_note) return null;
  return (
    <small className="fnote">
      {userName(users, flag.resolved_by, 'The engineer')}: “{flag.resolution_note}”
    </small>
  );
}

/** The note to the engineer: typed while the flag waits (saved when the box is left), read once it is closed. */
export function FlagNote({ flag, canEdit }: { flag: FlagRow; canEdit: boolean }) {
  const [text, setText] = useState(flag.note ?? '');
  if (flag.resolved_at || !canEdit) return flag.note ? <small className="fnote">Note to the engineer: {flag.note}</small> : null;
  return (
    <input
      className="fnote"
      type="text"
      value={text}
      placeholder="Note to the engineer (optional)"
      aria-label="Note to the engineer"
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if ((text || '') !== (flag.note ?? '')) void noteFlag(flag.id, text);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

/** The small form that raises a flag in the person's own words: what must change, an optional note, Flag. */
export function FlagForm({ what, onFlag, onCancel }: { what: string; onFlag: (text: string, note: string) => Promise<void>; onCancel: () => void }) {
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await onFlag(text.trim(), note.trim());
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="flagform"
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      <input type="text" autoFocus value={text} placeholder={`What must change ${what}`} aria-label={`Flag ${what}`} onChange={(e) => setText(e.target.value)} />
      <input type="text" value={note} placeholder="Note to the engineer (optional)" aria-label="Note to the engineer" onChange={(e) => setNote(e.target.value)} />
      <button type="submit" className="btn pri" disabled={!text.trim() || busy}>
        Flag
      </button>
      <button type="button" className="btn" onClick={onCancel}>
        Cancel
      </button>
    </form>
  );
}

/**
 * The flags on a wall or on the room, under the walk bar (Q5): each with its words, its state, the note; Undo while it waits,
 * Clear once the engineer closed it (an item's flags clear with the item's check; a wall has no check of its own).
 */
export function WallFlags({ ctx, flags, wallId, users, seats, canEdit }: { ctx: JobCtx; flags: FlagRow[]; wallId: string; users: Row[]; seats: Row[]; canEdit: boolean }) {
  const here = flags.filter((f) => !f.cleared_at && (wallId === 'room' ? f.target === 'room' : f.target === 'wall' && f.view_id === wallId));
  if (here.length === 0) return null;
  return (
    <div className="wflags">
      {here.map((f) => (
        <div key={f.id} className={`drow fx${f.resolved_at ? ' dn' : ''}`}>
          <div className="v">
            Flag <b>{f.text}</b>
            <small>on {wallId === 'room' ? 'the room' : 'this wall'}</small>
            <FlagNote flag={f} canEdit={canEdit} />
            <ClosingNote flag={f} users={users} />
          </div>
          <div className="mini">
            <FlagState flag={f} users={users} seats={seats} />
            {canEdit && !f.resolved_at && (
              <button type="button" className="btn" onClick={() => void undoFlag(f.id)}>
                Undo
              </button>
            )}
            {canEdit && f.resolved_at && (
              <button type="button" className="btn" title="Seen: take it off the screen" onClick={() => void clearFlag(f.id).then(() => toast('Flag cleared.'))}>
                Clear
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/** The flags showing on one item (open, or closed and not yet cleared): what the Quick row's chips read. */
export function itemFlags(flags: FlagRow[], itemId: string): FlagRow[] {
  return flags.filter((f) => f.job_item_id === itemId && !f.cleared_at);
}

